// Plan ⇄ composer-Draft mapping for the scheduler-composer's `single`
// mode (next/program-editor-redesign.md §5.6). A "Plan" is a
// single-workout template; the composer authors it with the SAME Draft
// model + set table it uses for programs, then this layer maps the
// single-session Draft to the Plan wire shape (`/api/me/plans`) and back.
//
// Reuse, not a second editor: `draftToNewPlan` mirrors buildPayload's
// validation + group de-singleton + symmetric-superset padding, and the
// set encoding goes through the SAME parseRepsToken / buildIntensity the
// program path uses, so reps + intensity behave identically across modes.
//
// SET-MODEL NOTE (decision 2026-06-24, union-reuse): the composer's
// SetIntensity is an EXCLUSIVE union — a set carries EITHER a weight
// prescription (absolute / %-of-max / history) OR an effort one (rpe /
// rir), never both. The Plan wire shape (PlannedSet) is richer: it can
// carry targetWeight AND targetRpe/targetRir on the same set (the legacy
// web PlansCard allowed it). So:
//   - draftToNewPlan: an `rpe`/`rir` row emits targetWeight:none +
//     targetRpe/targetRir; a weight row emits the weight + no effort.
//   - planToDraft: a set with a weight kind wins the visible column; if
//     it ALSO carried targetRpe/targetRir, that per-set effort value is
//     DROPPED from the draft (the column still round-trips via the
//     entry-level rpeEnabled/rirEnabled flag). This "weight + per-set
//     effort on the same set" gap is tracked in rnw-strangler §8.

import {
  NewPlan,
  type NewPlan as NewPlanT,
  type Plan as PlanT,
  type PlannedSet as PlannedSetT,
  type PlannedTargetWeight as PlannedTargetWeightT,
} from "./schemas/plan.js";
import type { SetIntensity as SetIntensityT } from "./schemas/program.js";
import {
  type CellDraft,
  type Draft,
  type IntensityKind,
  type SetRowDraft,
  type SlotDraft,
  type BuildReasonCode,
  buildIntensity,
  kindAt,
  parseRepsToken,
  amrapOf,
  buildWorkForCell,
  timeAxisError,
  amrapWithTimeError,
  VALUELESS_KINDS,
} from "./programEdit.js";
import { resizeSlotToWeeks } from "./programEditActions.js";
import { alignGroupSlots } from "./programEditRoundSets.js";
import { fromCanonicalKg } from "./units.js";
import type { UnitsPreference } from "./schemas/profile.js";
import { uuidv4 } from "./uuid.js";

// A single-mode Draft is a normal composer Draft pinned to cycleWeeks=1
// with exactly one session. The session name mirrors the plan name (the
// composer meta exposes `name` + `description`; session.name is internal
// plumbing that buildPayload-style validation requires non-empty).
const SINGLE_CYCLE_WEEKS = 1;

// Blank single-mode draft for a brand-new Plan. status is a placeholder:
// draftToNewPlan never serialises it (the server defaults Plan.status
// from scheduledFor), so the ProgramStatus value here is inert.
//
// slots starts EMPTY: the editor builds the exercise list by adding (the
// PlanComposer rework). A seeded blankSlot (exerciseId "") would derive into
// a ghost block — an empty-named list row that draftToNewPlan then rejects
// ("Entry has no exercise") since applyBlock never evicts it. An empty
// session is still non-serialisable (draftToNewPlan guards slots.length===0),
// so a nameless/exerciseless plan stays unsaveable, by design.
export const blankPlanDraft = (): Draft => ({
  name: "",
  description: "",
  // A plan has no localised fields — see Draft.editLocale.
  editLocale: null,
  status: "draft",
  cycleWeeks: SINGLE_CYCLE_WEEKS,
  sessionsPerWeek: 1,
  // A Plan carries no translations — it is the athlete's own
  // single-language entity — so every stored-value companion stays
  // absent and the build path emits naked strings.
  sessions: [{ id: uuidv4(), name: "", notes: "", slots: [] }],
  assignedTo: [],
  assigneeEmails: [],
});

// --- intensity ⇄ planned-set fields -------------------------------------

type PlannedEffort = Pick<PlannedSetT, "targetWeight" | "targetRpe" | "targetRir">;

const intensityToPlanned = (intensity: SetIntensityT): PlannedEffort => {
  switch (intensity.kind) {
    case "absolute":
      return { targetWeight: { kind: "absolute", weightKg: intensity.weightKg } };
    case "percent-of-max":
      return { targetWeight: { kind: "percentage", pctOfMax: intensity.pct } };
    case "history":
      return { targetWeight: { kind: "history" } };
    case "rpe":
      return { targetWeight: { kind: "none" }, targetRpe: intensity.target };
    case "rir":
      return { targetWeight: { kind: "none" }, targetRir: intensity.target };
    case "none":
    default:
      return { targetWeight: { kind: "none" } };
  }
};

// Reverse: pick the visible composer column for a PlannedSet. Weight wins
// when present; only a weightless set falls through to the per-set effort
// column. See the SET-MODEL NOTE — a weight set carrying targetRpe/Rir
// loses that per-set effort value here (rnw-strangler §8).
const plannedToRowCell = (
  set: PlannedSetT,
  cellUnit: UnitsPreference,
): { kind: IntensityKind; intensityValue: string } => {
  const w: PlannedTargetWeightT = set.targetWeight;
  if (w.kind === "absolute")
    return {
      kind: "absolute",
      intensityValue: String(fromCanonicalKg(w.weightKg, cellUnit)),
    };
  if (w.kind === "percentage") return { kind: "percent-of-max", intensityValue: String(w.pctOfMax) };
  if (w.kind === "history") return { kind: "history", intensityValue: "" };
  // w.kind === "none": effort column, else bare.
  if (set.targetRpe != null) return { kind: "rpe", intensityValue: String(set.targetRpe) };
  if (set.targetRir != null) return { kind: "rir", intensityValue: String(set.targetRir) };
  return { kind: "none", intensityValue: "" };
};

// --- Draft → NewPlan -----------------------------------------------------

export type PlanBuildResult =
  | { ok: true; payload: NewPlanT }
  | { ok: false; reason: string; code: BuildReasonCode };

export type PlanMeta = {
  // Calendar date "YYYY-MM-DD" or "" for an unscheduled (draft) plan.
  scheduledFor?: string;
  // Unit the draft's absolute cells are expressed in — MUST match the one
  // planToDraft seeded them with. ⛔ Required (CN-26 wave 3): an optional
  // field defaulting to "kg" is exactly the type-level defect the wave
  // removes — see programEdit.ts's programToDraft for the full rationale.
  cellUnit: UnitsPreference;
  // CO-17/CO-20 — same contract as buildPayload's own `authoredAs`
  // parameter (programEditPayload.ts): the caller passes this on the
  // create path only (the view active when the "+" was pressed) and
  // omits it on edit — a plan does not change hats, and the server
  // preserves the stored value regardless (plansLifecycle.ts). Absent ⇒
  // the schema's own default.
  authoredAs?: NewPlanT["authoredAs"];
};

export const draftToNewPlan = (draft: Draft, meta: PlanMeta): PlanBuildResult => {
  const cellUnit = meta.cellUnit;
  const session = draft.sessions[0];
  if (!session) return { ok: false, reason: "No session", code: "noSession" };
  if (draft.name.trim().length === 0)
    return { ok: false, reason: "Plan name empty", code: "planNameEmpty" };
  if (session.slots.length === 0)
    return { ok: false, reason: "Plan has no exercises", code: "planNoExercises" };

  // Validate every slot/row the same way buildPayload does (single week).
  for (const slot of session.slots) {
    if (slot.exerciseId.trim().length === 0)
      return { ok: false, reason: "Entry has no exercise", code: "slotNoExercise" };
    if (slot.setRows.length === 0)
      return { ok: false, reason: "Entry has no sets", code: "slotNoSets" };
    for (const row of slot.setRows) {
      const cell = row.cells[0];
      if (!cell) return { ok: false, reason: "Row width mismatch", code: "rowWidthMismatch" };
      if (parseRepsToken(cell.reps) == null)
        return { ok: false, reason: "Invalid reps", code: "invalidReps" };
      if (timeAxisError(row.workKind, cell.workDurationSec, cell.restSec))
        return {
          ok: false,
          reason: "Invalid work/rest seconds",
          code: "invalidSeconds",
        };
      if (amrapWithTimeError(row.workKind, amrapOf(cell)))
        return {
          ok: false,
          reason: "AMRAP floor on a clock-bounded bout",
          code: "amrapWithTime",
        };
      const kind = kindAt(row, 0);
      if (!VALUELESS_KINDS.has(kind)) {
        const intensity = buildIntensity(
          kind,
          cell.intensityValue,
          cellUnit,
        );
        if ("error" in intensity)
          return { ok: false, reason: intensity.error, code: intensity.code };
      }
    }
  }

  // Reject mixed kinds inside a single group id (defense-in-depth, mirrors
  // buildPayload + WorkoutLog).
  const seenKind = new Map<string, string>();
  for (const slot of session.slots) {
    if (!slot.group) continue;
    const prior = seenKind.get(slot.group.id);
    if (prior && prior !== slot.group.kind)
      return {
        ok: false,
        reason: `mixed kinds for group ${slot.group.id}`,
        code: "groupKindMixed",
      };
    seenKind.set(slot.group.id, slot.group.kind);
  }

  // Drop singleton groups (a lone "Superset A" without a partner).
  const counts = new Map<string, number>();
  for (const slot of session.slots)
    if (slot.group) counts.set(slot.group.id, (counts.get(slot.group.id) ?? 0) + 1);
  const deSingletoned: SlotDraft[] = session.slots.map((slot) =>
    slot.group && (counts.get(slot.group.id) ?? 0) < 2 ? { ...slot, group: null } : slot,
  );

  // Force symmetric supersets: pad each member up to its group's longest
  // by repeating its last set, with no note (superset = interleaved rounds).
  // The program save's own rule (alignGroupSlots, PE-30, #336).
  const groupIds = new Set(deSingletoned.flatMap((slot) => (slot.group ? [slot.group.id] : [])));
  const padded = [...groupIds].reduce((acc, gid) => alignGroupSlots(acc, gid, "pad"), deSingletoned);

  const entries = padded.map((slot) => {
    const sets: PlannedSetT[] = slot.setRows.map((row) => {
      const cell = row.cells[0]!;
      const tok = parseRepsToken(cell.reps) ?? { reps: 0, amrap: false };
      const amrap = amrapOf(cell);
      const kind = kindAt(row, 0);
      const intensity: SetIntensityT =
        kind === "none"
          ? { kind: "none" }
          : (buildIntensity(
              kind,
              cell.intensityValue,
              cellUnit,
            ) as SetIntensityT);
      const notes = (row.notesPerCell[0] ?? "").trim();
      // Same ONE helper the program build uses (HI-1): the two emissions
      // were byte-identical, and a rule this quiet drifts the moment it
      // lives twice.
      const { amrapAllowed, ...time } = buildWorkForCell(
        row.workKind,
        cell.workDurationSec,
        cell.restSec,
      );
      return {
        targetReps: tok.reps,
        ...intensityToPlanned(intensity),
        ...(amrap && amrapAllowed ? { amrap: true } : {}),
        ...time,
        ...(notes ? { notes } : {}),
      };
    });
    return {
      exerciseId: slot.exerciseId,
      ...(slot.exerciseName ? { exerciseName: slot.exerciseName } : {}),
      sets,
      ...(slot.instructions.trim() ? { notes: slot.instructions.trim() } : {}),
      ...(slot.rpeEnabled ? { rpeEnabled: true } : {}),
      ...(slot.rirEnabled ? { rirEnabled: true } : {}),
      ...(slot.group
        ? {
            group: {
              id: slot.group.id,
              kind: slot.group.kind,
              // PE-30 — a group's name is destroyed, not merely unread, by a
              // rebuild that omits it: this shape is written back OVER the
              // stored group on every plan save.
              ...(slot.group.name !== undefined ? { name: slot.group.name } : {}),
            },
          }
        : {}),
    };
  });

  const scheduledFor = (meta.scheduledFor ?? "").trim();
  const payload: NewPlanT = {
    name: draft.name.trim(),
    // CO-17 — see PlanMeta.authoredAs above: only ever supplied on create.
    authoredAs: meta.authoredAs ?? "athlete",
    ...(draft.description.trim() ? { notes: draft.description.trim() } : {}),
    ...(scheduledFor ? { scheduledFor } : {}),
    entries,
    // Preserve existing assignees; new emails ride alongside (server
    // resolves + dedupes), mirroring the program path.
    ...(draft.assignedTo.length > 0 ? { assignedTo: draft.assignedTo } : {}),
    ...(draft.assigneeEmails.length > 0 ? { assignedToEmails: draft.assigneeEmails } : {}),
  };

  const validated = NewPlan.safeParse(payload);
  if (!validated.success) {
    const first = validated.error.issues[0];
    const path = first?.path.join(".") ?? "?";
    return {
      ok: false,
      reason: `${path}: ${first?.message ?? ""}`,
      code: "schemaInvalid",
    };
  }
  return { ok: true, payload: validated.data };
};

// --- Plan → Draft (edit) -------------------------------------------------

const plannedSetToRow = (
  set: PlannedSetT,
  cellUnit: UnitsPreference,
): SetRowDraft => {
  const { kind, intensityValue } = plannedToRowCell(set, cellUnit);
  const cell: CellDraft = { reps: String(set.targetReps), intensityValue };
  if (set.amrap) cell.amrap = true;
  // Time axis (HI-1). WITHOUT this the plan editor is a data shredder for
  // interval work: a plan started from a HIIT program carries the bouts,
  // and the first save from this screen would rebuild every set without
  // them — silently, since nothing on screen shows what was dropped.
  if (set.work?.kind === "countdown")
    cell.workDurationSec = String(set.work.durationSec);
  if (set.restSec !== undefined) cell.restSec = String(set.restSec);
  const row: SetRowDraft = {
    intensityKind: kind,
    cells: [cell],
    notesPerCell: [set.notes ?? ""],
  };
  if (set.work) row.workKind = set.work.kind;
  return row;
};

// `cellUnit`: same contract as programToDraft — the unit the absolute cells
// come out in, and the one draftToNewPlan must be given on the way back.
// ⛔ Required (CN-26 wave 3): see programToDraft.
export const planToDraft = (
  plan: PlanT,
  cellUnit: UnitsPreference,
): { draft: Draft; scheduledFor: string } => {
  // PE-49c, same reasoning as PE-47's blankSlot and PE-49b's addSession:
  // mint the id HERE, on the client, rather than leave it empty for the
  // server to stamp at save. Two entries imported into the same draft used
  // to share id "", so rowIdOf/resolveActiveId in the logger's hydration
  // layer always resolved to the first one and findSlugConflict in the
  // composer could not tell the slot being edited apart from any other
  // still-unsaved slot. A saved plan's entries have no id of their own to
  // carry across (PlannedEntry has none), so every round trip through
  // planToDraft mints a fresh one; that is fine, the server keeps whatever
  // id a slot already had on save (`slot.id || randomUUID()`) and only
  // stamps when one is truly missing.
  const slots: SlotDraft[] = plan.entries.map((entry) => ({
    id: uuidv4(),
    exerciseId: entry.exerciseId,
    exerciseName: entry.exerciseName,
    setRows: entry.sets.map((set) => plannedSetToRow(set, cellUnit)),
    instructions: entry.notes ?? "",
    amrapLastSet: false,
    maxAttempt: false,
    rpeEnabled: entry.rpeEnabled ?? false,
    rirEnabled: entry.rirEnabled ?? false,
    group: entry.group
      ? {
          id: entry.group.id,
          kind: entry.group.kind,
          ...(entry.group.name !== undefined ? { name: entry.group.name } : {}),
        }
      : null,
    variantsRaw: [],
    inactiveWeeks: [false],
  }));

  const draft: Draft = {
    name: plan.name,
    description: plan.notes ?? "",
    editLocale: null,
    status: "draft",
    cycleWeeks: SINGLE_CYCLE_WEEKS,
    sessionsPerWeek: 1,
    sessions: [{ id: uuidv4(), name: plan.name, notes: "", slots }],
    assignedTo: plan.assignedTo.filter((s) => s !== ""),
    assigneeEmails: [],
  };
  return { draft, scheduledFor: plan.scheduledFor ?? "" };
};

/** PE-40 — a saved workout, as slots a PROGRAM day can hold.
 *
 *  ⭐ Goes through `planToDraft` and takes its session's slots rather than
 *  re-walking `plan.entries`: that mapping is where a PlannedSet becomes a
 *  cell (`plannedSetToRow` — weight kind wins over effort, deterministically),
 *  and a second copy of it would be free to drift on the day either side
 *  changes. The intermediate Draft is thrown away; the mapping is the point.
 *
 *  ⛔ The resize is NOT optional. `planToDraft` builds at `cycleWeeks: 1`
 *  because a Plan is one workout, so every per-week array arrives length 1;
 *  dropped into a 6-week day unresized, every reader of `cells[w]` past week 0
 *  reads `undefined`. `resizeSlotToWeeks` fills by copying, which is also what
 *  a coach means by importing a session into a mesocycle: the same work, every
 *  week, until they change it.
 *
 *  ⭐ Slot ids come out as client-minted uuids (PE-49c), same as `blankSlot`
 *  (PE-47) and `addSession` (PE-49b): a slot the program has never persisted
 *  still needs an id THIS session can tell apart from every other slot the
 *  day holds, or the logger's hydration layer resolves them all to the same
 *  one. The server keeps a client-minted id at save (`slot.id || randomUUID()`)
 *  exactly as it does for those two. */
export const planToSlotDrafts = (
  plan: PlanT,
  cellUnit: UnitsPreference,
  cycleWeeks: number,
): SlotDraft[] =>
  (planToDraft(plan, cellUnit).draft.sessions[0]?.slots ?? []).map((slot) =>
    resizeSlotToWeeks(slot, cycleWeeks),
  );
